import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { cardData, DashboardCardsDto, DashboardChartsDto, DashboardProvinceDto, DashboardRegioniDto, DashboardTitoliDto, IscrittoYearsDto } from '../../model/dashboard-data';
import { environment } from '../../environment/environment';
import { IscrittiService } from '../../services/iscritti.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, OnDestroy{

  // prova per la gestione della regione per i componenti
  labelRegionale = environment.labelRegionale;
  regioniAnaliticaInformazioniIscritti = environment.regioniAnaliticaInformazioniIscritti;

  // parametri cards
  cards: cardData[] = environment.cards;

  //skeleton_loaders
  cardsLoading: boolean = true;
  yearsLoading: boolean = true;
  chartsLoading: boolean = true;

  //services
  private iscritti_service = inject(IscrittiService);
  public translate = inject(TranslateService);

  //elementi per la tabella
  annoPiccolo: number | undefined;
  provvisori: number | undefined;
  riepiloghi: IscrittoYearsDto[] = [];
  skeletonArray: Number[] = [1, 2, 3, 4, 5, 6];

  //dati dei grafici
  dataSesso: any;
  optionsSesso: any;

  dataEta: any;
  optionsEta: any;

  dataTitoli: any;
  optionsTitoli: any;

  dataRegioni: any;
  optionsRegioni: any;

  dataProvince: any;
  optionsProvince: any;

  changeLanguage: Subscription|undefined;

  ngOnInit(): void {
    this.loadDashboard();
    this.changeLanguage = this.translate.onLangChange.subscribe(() => {
      this.loadDashboard();
    });
  }
  ngOnDestroy(){
    this.changeLanguage?.unsubscribe();
  }

  //dati della dashboard
  loadDashboard(){
    this.assegnaValoriCards();
    this.caricaTabella();

    this.iscritti_service.getDashboardCharts().subscribe({
      next: (chartsResult: DashboardChartsDto) => {
        if(chartsResult){
          this.sessoGrafico(chartsResult);
          this.etaGrafico(chartsResult);
        }
      },
      complete: () => {
        this.chartsLoading = false;
      },
      error: (err) => {
        console.log(err);
      }
    });

    if(this.regioniAnaliticaInformazioniIscritti){
      this.getTitoli();
      this.getRegioni();
      this.getProvince();
    }

  }

  private assegnaValoriCards(){
    this.iscritti_service.getDashboardCards().subscribe({
      next: (cardsResult: DashboardCardsDto) => {
        if(cardsResult){
          this.cards.forEach(card => {
            if(card.type === 'provvisori') card.value = cardsResult.provvisori;
            if(card.type === 'morosità') card.value = cardsResult.morosi;
            if(card.type === 'trasferimento') card.value = cardsResult.trasferimenti;
            if(card.type === 'sospesi') card.value = cardsResult.sospesi;
          });
        }
      },
      complete: () => { this.cardsLoading = false },
      error: (err) => { console.log(err) }
    });
  }

  private caricaTabella(){
    this.iscritti_service.getDashboardYears().subscribe({
      next: (yearsResult: IscrittoYearsDto[]) => {
        this.riepiloghi = [];
        if(yearsResult){
          this.riepiloghi.push(...yearsResult);
          this.annoPiccolo = this.estraiAnnoMinore(this.riepiloghi);
        }
      },
      complete: () => { this.yearsLoading = false },
      error: (err) => { console.log(err) }
    });
  }

  //costruzione grafico uomini/donne
  private sessoGrafico(result: DashboardChartsDto){

    this.dataSesso = {
      labels: [this.translate.instant('dashboard.analitica.donne'), this.translate.instant('dashboard.analitica.uomini')],
      datasets: [
        {
          data: [result.donne, result.uomini],
          backgroundColor: ["#F9A8D4", "#A5B4FC"],
          hoverBackgroundColor: ["#ffcee8", "#c9d3ff"],
          borderRadius: '5'
        }
      ]
    };

    //opzioni del grafico
    this.optionsSesso = {
      responsive: true,
      maintainAspectRatio: true,
      borderWidth: '0',
      border: '0',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 25,
            font: {
              size: 20
            }
          }
        }
      }
    };
  }

  //costruzione grafico dati anagrafici
  private etaGrafico(result: DashboardChartsDto){

    this.dataEta = {
      labels: ['Under 30', 'Over 50', '30 - 40'],
      datasets: [
        {
          data: [result.underTrenta, result.overCinquanta, result.traTrentaCinquanta],
          backgroundColor: ["#aae0bd", "#f2c05c" , "#f7ef56"],
          hoverBackgroundColor: ["#c5e5d0", "#f9d793", "#fff6bf"],
          borderRadius: '5'
        }
      ]
    };

    //opzioni del grafico
    this.optionsEta = {
      responsive: true,
      maintainAspectRatio: true,
      borderWidth: '0',
      border: '0',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 25,
            font: {
              size: 17
            }
          }
        }
      }
    };
  }

  //costruzione grafico titoli
  private titoliGrafico(result: DashboardTitoliDto[]){
    this.dataTitoli = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [
            "#59ADF6",
            "#9D94FF",
            "#2f4ce0",
            "#C780E8"
          ],
          hoverBackgroundColor: [
            "#94caf7",
            "#c2bdfc",
            "#798be5",
            "#d0a5e5"
          ],
          borderRadius: '5'
        }
      ]
    };
    for(const titolo of result){
      this.dataTitoli.labels.push(titolo.nome);
      this.dataTitoli.datasets[0].data.push(titolo.conteggio);
    }
    //opzioni del grafico
    this.optionsTitoli = {
      responsive: true,
      maintainAspectRatio: true,
      borderWidth: '0',
      border: '0',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 25,
            font: {
              size: 17
            }
          }
        }
      }
    };
  }
  //costruzione grafico regioni
  private regioniGrafico(result: DashboardRegioniDto){
    this.dataRegioni = {
      labels: [this.labelRegionale, this.translate.instant('dashboard.analiticaInfo.fuori'), this.translate.instant('dashboard.analiticaInfo.estero')],
      datasets: [
        {
          data: [result.regione, result.fuoriRegione, result.estero],
          backgroundColor: ["#4C936F", "#BC5839","#9AAAD1" ],
          hoverBackgroundColor: ["#77BE9C", "#d38974","#B8C3E7" ],
          borderRadius: '5'
        }
      ]
    };
    //opzioni del grafico
    this.optionsRegioni = {
      responsive: true,
      maintainAspectRatio: true,
      borderWidth: '0',
      border: '0',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 25,
            font: {
              size: 17
            }
          }
        }
      }
    };
  }
  //costruzione grafico province
  private provinceGrafico(result: DashboardProvinceDto){

    this.dataProvince = {
      labels: [...result.provinceNomi, this.translate.instant('dashboard.analiticaInfo.altro')],
      datasets: [
        {
          data: [...result.province, result.altro],
          backgroundColor: [
            "#4C936F", "#BC5839", "#9AAAD1", "#F0A500",
            "#7B5EA7", "#E07B54", "#4A90D9", "#D4A017",
            "#5C8A5A", "#C2556A", "#6AB0B0", "#A07850"
          ],
          hoverBackgroundColor: [
            "#77BE9C", "#d38974", "#B8C3E7", "#F5C842",
            "#A88EC7", "#E8A080", "#7AB0E8", "#E0C050",
            "#85B082", "#D47A90", "#8FCFCF", "#C0A07A"
          ],
          borderRadius: '5'
        }
      ]
    };
    //opzioni del grafico
    this.optionsProvince = {
      responsive: true,
      maintainAspectRatio: true,
      borderWidth: '0',
      border: '0',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 25,
            font: {
              size: 17
            }
          }
        }
      }
    };
  }

  //estrai anno minore per gestione grafica della tabella
  private estraiAnnoMinore(riepiloghi: IscrittoYearsDto[]): number{
    let anni: number[] = [];
    let anno: number = 0;

    riepiloghi.forEach( riepilogo => anni.push(riepilogo.anno) );

    anno = Math.min(...anni);

    return anno;
  }

  // caricamenti ----------------

  private getTitoli(){
    this.iscritti_service.getDashboardTitoli().subscribe({
      next: (result: DashboardTitoliDto[]) => {
        this.titoliGrafico(result);
      },
      error: err => {
        console.log(err);
      }
    })
  }

  private getRegioni(){
    this.iscritti_service.getDashboardRegioni(this.labelRegionale).subscribe({
      next: (result: DashboardRegioniDto) => {
        this.regioniGrafico(result);
      },
      error: err => {
        console.log(err);
      }
    })
  }

  private getProvince(){
    this.iscritti_service.getDashboardProvince(this.labelRegionale).subscribe({
      next: (result: DashboardProvinceDto) => {
        this.provinceGrafico(result);
      },
      error: err => {
        console.log(err);
      }
    })
  }

}
